import type { Locale } from "@planerius/contracts"

/**
 * Язык интерфейса: словари и то, какие языки МОЖНО предложить.
 *
 * ПОЧЕМУ НЕ БИБЛИОТЕКА. Здесь нужно ровно три вещи: взять строку по ключу, подставить
 * в неё число или имя и знать, какие языки готовы. Библиотека i18n приносит ленивую
 * загрузку словарей, форматирование по ICU, детект по заголовкам браузера и свой
 * контекст на всё приложение — и ни одно из этого сегодня не нужно, зато каждое стоит
 * размера сборки и одного места, где можно ошибиться молча.
 *
 * ПОЛНОТУ СЛОВАРЯ ПРОВЕРЯЕТ ТИП, А НЕ ВНИМАТЕЛЬНОСТЬ. Каждый словарь объявлен как
 * `Record<TextKey, string>`: забытый ключ — ошибка сборки, а не строка «undefined»
 * посреди экрана у человека, который и без того читает интерфейс на неродном языке.
 *
 * ПЕРЕВОДОВ СЕЙЧАС ДВА — И ПРЕДЛАГАЕМ МЫ РОВНО ДВА. Казахский объявлен в контракте
 * и в базе (решение владельца 26: многоязычность с самого начала), но словаря у него нет,
 * и показывать его в переключателе значит обещать перевод, которого не будет: человек
 * выберет его один раз, увидит русский интерфейс и решит, что переключатель сломан.
 */

/** Ключи переводимых строк. Плоские, без вложенности: иначе поиск по коду перестаёт работать. */
export type TextKey =
  // Разделы кабинета
  | "nav.summary"
  | "nav.notifications"
  | "nav.journal"
  | "nav.clients"
  | "nav.catalog"
  | "nav.staff"
  | "nav.branches"
  | "nav.reports"
  | "nav.settings"
  | "nav.channels"
  | "nav.campaigns"
  | "nav.audit"
  | "nav.billing"
  | "nav.developers"
  | "nav.support"
  // Иконочная навигация 56 px: две буквы на раздел.
  //
  // СОКРАЩЕНИЕ — ЧАСТЬ ПЕРЕВОДА, А НЕ ПЕРВЫЕ ДВЕ БУКВЫ ПЕРЕВОДА. Оно считалось правилом
  // «label.slice(0, 2)» с таблицей исключений, и таблица была ключом по РУССКОМУ тексту:
  // по-английски «Services» и «Settings» дают одно и то же «Se», а «Schedule» — третье
  // «Sc», и различить первые два в столбце 56 px нельзя ничем, кроме подсказки, которой
  // у планшета нет. Тип ниже требует сокращение от КАЖДОГО языка, а сторож проверяет,
  // что внутри языка они не повторяются.
  | "navShort.summary"
  | "navShort.notifications"
  | "navShort.journal"
  | "navShort.clients"
  | "navShort.catalog"
  | "navShort.staff"
  | "navShort.branches"
  | "navShort.reports"
  | "navShort.settings"
  | "navShort.channels"
  | "navShort.campaigns"
  | "navShort.audit"
  | "navShort.billing"
  | "navShort.developers"
  | "navShort.support"
  // Оболочка
  | "shell.cabinet"
  | "shell.profile"
  | "shell.today"
  | "shell.retry"
  | "shell.offline"
  | "shell.dataAt"
  | "shell.allBranches"
  // Профиль: выбор языка
  | "profile.language"
  | "profile.languageHint"
  | "profile.languageSaved"
  | "profile.languageFailed"

type Dictionary = Readonly<Record<TextKey, string>>

const ru: Dictionary = {
  "nav.summary": "Сводка",
  "nav.notifications": "Уведомления",
  "nav.journal": "Журнал",
  "nav.clients": "Клиенты",
  "nav.catalog": "Каталог",
  "nav.staff": "Сотрудники",
  "nav.branches": "Филиалы",
  "nav.reports": "Отчёты",
  "nav.settings": "Настройки",
  "nav.channels": "Каналы",
  "nav.campaigns": "Рассылки и сегменты",
  "nav.audit": "Журнал действий",
  "nav.billing": "Тариф и оплата",
  "nav.developers": "Разработчикам",
  "nav.support": "Поддержка",

  // Сокращения по ударным буквам; пары, которые иначе совпали бы, дизайнер разобрал:
  // Каталог/Каналы и Журнал/Журнал действий.
  "navShort.summary": "Св",
  // «Ув», а не «Ун»: рядом стоят «Настройки» (На) и «Каналы» (Кн) — на «н» уже два.
  "navShort.notifications": "Ув",
  "navShort.journal": "Жу",
  "navShort.clients": "Кл",
  "navShort.catalog": "Кт",
  "navShort.staff": "Со",
  "navShort.branches": "Фи",
  "navShort.reports": "От",
  "navShort.settings": "На",
  "navShort.channels": "Кн",
  "navShort.campaigns": "Рс",
  "navShort.audit": "Жд",
  "navShort.billing": "Та",
  "navShort.developers": "Ра",
  "navShort.support": "По",

  "shell.cabinet": "Кабинет",
  "shell.profile": "Профиль",
  "shell.today": "Сегодня",
  "shell.retry": "Повторить",
  "shell.offline": "Нет связи",
  "shell.dataAt": "Данные на {time}",
  "shell.allBranches": "Все филиалы",

  "profile.language": "Язык интерфейса",
  "profile.languageHint":
    "Меняется только у вас: у остальных сотрудников салона язык свой. Сообщения клиентам он не затрагивает — они идут на языке клиента.",
  "profile.languageSaved": "Язык изменён",
  "profile.languageFailed": "Не удалось сменить язык",
}

/**
 * Английский.
 *
 * ПЕРЕВОД, А НЕ ПОДСТРОЧНИК. «Сводка» — это не «Summary» в смысле «итог документа»,
 * а обзор дня: «Today at a glance» длинно для бокового меню, поэтому «Overview».
 * «Журнал» — не «Log», а расписание на день: «Schedule». «Журнал действий» — именно
 * «Activity log», и путать их нельзя: это два разных раздела, и человек, увидевший
 * два «Log», откроет не тот.
 */
const en: Dictionary = {
  "nav.summary": "Overview",
  "nav.notifications": "Notifications",
  "nav.journal": "Schedule",
  "nav.clients": "Clients",
  "nav.catalog": "Services",
  "nav.staff": "Staff",
  "nav.branches": "Locations",
  "nav.reports": "Reports",
  "nav.settings": "Settings",
  "nav.channels": "Channels",
  "nav.campaigns": "Campaigns and segments",
  "nav.audit": "Activity log",
  "nav.billing": "Plan and billing",
  "nav.developers": "Developers",
  "nav.support": "Support",

  /*
   * Сокращения английского РАЗОБРАНЫ ПОПАРНО, а не срезаны по две буквы: Services,
   * Settings, Schedule, Staff и Support дают на первых двух буквах три совпадения.
   * Services → Sv, Settings → Se, Schedule → Sc, Staff → St, Support → Su.
   */
  "navShort.summary": "Ov",
  // Notifications → No: «Ni» читается как имя, а «Nt» не читается вовсе.
  "navShort.notifications": "No",
  "navShort.journal": "Sc",
  "navShort.clients": "Cl",
  "navShort.catalog": "Sv",
  "navShort.staff": "St",
  "navShort.branches": "Lo",
  "navShort.reports": "Re",
  "navShort.settings": "Se",
  "navShort.channels": "Ch",
  "navShort.campaigns": "Ca",
  "navShort.audit": "Al",
  "navShort.billing": "Pb",
  "navShort.developers": "De",
  "navShort.support": "Su",

  "shell.cabinet": "Workspace",
  "shell.profile": "Profile",
  "shell.today": "Today",
  "shell.retry": "Retry",
  "shell.offline": "No connection",
  "shell.dataAt": "Data as of {time}",
  "shell.allBranches": "All locations",

  "profile.language": "Interface language",
  "profile.languageHint":
    "Applies to you only: every member of the salon has their own. Client messages are not affected — they go in the client's language.",
  "profile.languageSaved": "Language changed",
  "profile.languageFailed": "Could not change the language",
}

const DICTIONARIES: Partial<Record<Locale, Dictionary>> = { ru, en }

/** Язык по умолчанию: на нём сдаётся продукт и на нём написан исходный текст. */
export const FALLBACK_LOCALE: Locale = "ru"

/**
 * Языки, которые МОЖНО предложить человеку.
 *
 * Считается из словарей, а не перечисляется рядом с ними: список, объявленный отдельно,
 * однажды разойдётся со словарями — и в переключателе появится язык без перевода.
 */
export const READY_LOCALES: readonly Locale[] = Object.keys(DICTIONARIES) as Locale[]

/** Как язык называется НА СЕБЕ: «English», а не «Английский». */
export const LOCALE_NAMES: Readonly<Record<Locale, string>> = {
  ru: "Русский",
  en: "English",
  kk: "Қазақша",
}

/**
 * Строка по ключу с подстановкой.
 *
 * НЕИЗВЕСТНЫЙ ЯЗЫК ОТДАЁТ РУССКИЙ, А НЕ ПУСТОТУ. В учётке может лежать язык, словарь
 * которого ещё не написан (казахский уже можно записать через API): показать пустой
 * интерфейс хуже, чем показать русский.
 */
export function text(
  locale: Locale,
  key: TextKey,
  values: Readonly<Record<string, string | number>> = {},
): string {
  const dictionary = DICTIONARIES[locale] ?? DICTIONARIES[FALLBACK_LOCALE]
  const template = dictionary?.[key] ?? key
  return template.replace(/\{(\w+)\}/gu, (match, name: string) => {
    const value = values[name]
    return value === undefined ? match : String(value)
  })
}
